// Utilidades para manejar la comunicación con el Service Worker y actualizaciones

/**
 * Obtiene la versión actual del service worker
 */
export const getServiceWorkerVersion = async () => {
    if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker no soportado en este navegador');
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        if (!registration.active) {
            console.warn('No hay service worker activo');
            return null;
        }

        console.log('📡 Solicitando versión al service worker...');

        return new Promise((resolve) => {
            const messageChannel = new MessageChannel();
            messageChannel.port1.onmessage = (event) => {
                console.log('📬 Respuesta del SW:', event.data);
                if (event.data.type === 'VERSION_RESPONSE') {
                    console.log('✅ Versión recibida:', event.data.version);
                    resolve(event.data.version);
                } else {
                    console.warn('❓ Respuesta inesperada del SW:', event.data);
                    resolve(null);
                }
            };

            registration.active.postMessage(
                { type: 'GET_VERSION' },
                [messageChannel.port2]
            );

            // Timeout después de 5 segundos
            setTimeout(() => {
                console.warn('⏰ Timeout obteniendo versión del SW');
                resolve(null);
            }, 5000);
        });
    } catch (error) {
        console.error('❌ Error al obtener versión del SW:', error);
        return null;
    }
};

/**
 * Fuerza una actualización completa de la aplicación
 */
export const forceAppUpdate = async () => {
    try {
        console.log('Iniciando actualización forzada...');

        // 1. Enviar mensaje al service worker para que se prepare para desregistrarse
        if ('serviceWorker' in navigator) {
            try {
                const registration = await navigator.serviceWorker.ready;
                if (registration.active) {
                    registration.active.postMessage({ type: 'FORCE_UPDATE' });
                }
            } catch (error) {
                console.warn('No se pudo comunicar con el service worker:', error);
            }
        }

        // 2. Esperar un momento para que el SW procese el mensaje
        await new Promise(resolve => setTimeout(resolve, 500));

        // 3. Desregistrar todos los service workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            console.log('Desregistrando', registrations.length, 'service workers...');
            await Promise.all(
                registrations.map(async (registration) => {
                    console.log('Desregistrando SW:', registration.scope);
                    return registration.unregister();
                })
            );
        }

        // 4. Limpiar TODAS las cachés del navegador
        const cacheNames = await caches.keys();
        console.log('Eliminando', cacheNames.length, 'cachés:', cacheNames);
        await Promise.all(
            cacheNames.map(async (cacheName) => {
                console.log('Eliminando caché:', cacheName);
                return caches.delete(cacheName);
            })
        );

        // 5. Limpiar almacenamiento local relacionado con la app
        const keysToRemove = [];
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('exceliaone-') ||
                key.startsWith('sync-') ||
                key.startsWith('auth-') ||
                key.startsWith('dexie-') ||
                key.includes('cache') ||
                key.includes('version')) {
                keysToRemove.push(key);
            }
        });

        console.log('Limpiando localStorage keys:', keysToRemove);
        keysToRemove.forEach(key => localStorage.removeItem(key));

        // 6. Limpiar sessionStorage también
        Object.keys(sessionStorage).forEach(key => {
            if (key.startsWith('exceliaone-') ||
                key.startsWith('sync-') ||
                key.startsWith('auth-')) {
                sessionStorage.removeItem(key);
            }
        });

        // 7. Limpiar IndexedDB si es posible
        try {
            if ('indexedDB' in window) {
                // Intentar eliminar bases de datos conocidas
                const dbsToDelete = ['ExceliaOneDB', 'sync-db', 'offline-db'];
                for (const dbName of dbsToDelete) {
                    try {
                        await new Promise((resolve, reject) => {
                            const deleteReq = indexedDB.deleteDatabase(dbName);
                            deleteReq.onsuccess = () => resolve();
                            deleteReq.onerror = () => reject(deleteReq.error);
                            deleteReq.onblocked = () => {
                                console.warn(`DB ${dbName} bloqueada, no se pudo eliminar`);
                                resolve();
                            };
                        });
                        console.log(`Base de datos ${dbName} eliminada`);
                    } catch (error) {
                        console.warn(`No se pudo eliminar DB ${dbName}:`, error);
                    }
                }
            }
        } catch (error) {
            console.warn('Error al limpiar IndexedDB:', error);
        }

        console.log('Actualización forzada completada - todo limpio');
        return true;
    } catch (error) {
        console.error('Error durante la actualización forzada:', error);
        throw error;
    }
};

/**
 * Verifica si hay actualizaciones disponibles
 */
export const checkForUpdates = async () => {
    if (!('serviceWorker' in navigator)) {
        return false;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
        return true;
    } catch (error) {
        console.error('Error al verificar actualizaciones:', error);
        return false;
    }
};

/**
 * Compara versiones para determinar si están sincronizadas
 */
export const compareVersions = (appVersion, swVersion) => {
    if (!swVersion) return { status: 'unknown', message: 'Versión del caché no disponible' };

    if (appVersion === swVersion) {
        return { status: 'synced', message: 'Versiones sincronizadas' };
    }

    return { status: 'mismatch', message: 'Versiones diferentes detectadas' };
};

/**
 * Escucha cambios en el estado de conexión
 */
export const createConnectionListener = (onOnline, onOffline) => {
    const handleOnline = () => onOnline?.();
    const handleOffline = () => onOffline?.();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
    };
}; 